import type { CategoryResult, Signal } from '../collectors/types';
import { explain } from '../content/explanations';
import { h, nextId } from './dom';

export type BitsLevel = 'none' | 'low' | 'medium' | 'high';

export function bitsLevel(bits: number | null): BitsLevel {
  if (bits === null || bits <= 0) return 'none';
  if (bits < 1) return 'low';
  if (bits < 4) return 'medium';
  return 'high';
}

const LEVEL_TEXT: Record<BitsLevel, string> = { none: '', low: 'Low', medium: 'Medium', high: 'High' };

export function bitsLabel(bits: number | null): string {
  if (bits === null) return 'Not scored';
  const level = bitsLevel(bits);
  const n = bits >= 10 ? bits.toFixed(0) : bits.toFixed(1);
  return level === 'none' ? '0 bits' : `${n} bits · ${LEVEL_TEXT[level]}`;
}

/** Bits shown on a 0–10 scale. */
const METER_MAX = 10;
const TRUNCATE_CHARS = 300;
const TRUNCATE_LINES = 6;

function truncate(text: string): string | null {
  const lines = text.split('\n');
  if (text.length <= TRUNCATE_CHARS && lines.length <= TRUNCATE_LINES) return null;
  let out = lines.slice(0, TRUNCATE_LINES).join('\n');
  if (out.length > TRUNCATE_CHARS) out = out.slice(0, TRUNCATE_CHARS);
  return `${out}…`;
}

function valueBlock(s: Signal): HTMLElement {
  const short = truncate(s.display);
  const code = h('code', { class: 'value' }, short ?? s.display);
  if (!short) return h('div', { class: 'signal-value' }, code);

  const button = h('button', { type: 'button', class: 'link-button', 'aria-expanded': 'false' }, 'Show full value');
  button.addEventListener('click', () => {
    const expanded = button.getAttribute('aria-expanded') === 'true';
    code.textContent = expanded ? short : s.display;
    button.setAttribute('aria-expanded', String(!expanded));
    button.textContent = expanded ? 'Show full value' : 'Show less';
  });
  return h('div', { class: 'signal-value' }, code, button);
}

function meter(bits: number | null): HTMLElement {
  const fill = h('span', { class: 'meter-fill' });
  const pct = bits === null ? 0 : Math.min(100, (Math.max(0, bits) / METER_MAX) * 100);
  // CSSOM property writes are allowed under `style-src 'self'`; only the style attribute is not.
  fill.style.setProperty('--fill', `${pct.toFixed(1)}%`);
  return h('span', { class: 'meter', 'aria-hidden': 'true' }, fill);
}

/** Called when a "What if this were hidden?" toggle changes. */
export type WhatIfHandler = (id: string, hidden: boolean) => void;

function whatIfToggle(s: Signal, onWhatIf: WhatIfHandler | undefined, row: () => HTMLElement): HTMLElement | null {
  if (!onWhatIf || s.bits === null || s.bits <= 0) return null;
  const input = h('input', { type: 'checkbox', 'data-signal': s.id });
  input.addEventListener('change', () => {
    row().toggleAttribute('data-hidden', input.checked);
    onWhatIf(s.id, input.checked);
  });
  return h('label', { class: 'whatif-toggle' }, input, ' What if this were hidden?');
}

export function renderSignal(s: Signal, onWhatIf?: WhatIfHandler): HTMLLIElement {
  const level = bitsLevel(s.bits);
  const info = explain(s.id);
  const labelId = nextId('sig');
  const status = s.randomized ? 'randomized' : !s.available ? 'unavailable' : '';
  const li: HTMLLIElement = h(
    'li',
    { class: 'signal', 'data-level': level, 'data-status': status || null },
    h(
      'div',
      { class: 'signal-head' },
      h('h4', { class: 'signal-label', id: labelId }, s.label),
      h('span', { class: 'bits', title: 'Estimated identifying bits' }, meter(s.bits), h('span', { class: 'bits-text' }, bitsLabel(s.bits))),
    ),
    valueBlock(s),
    whatIfToggle(s, onWhatIf, () => li),
    s.note ? h('p', { class: 'signal-note' }, status ? h('span', { class: 'tag' }, status === 'randomized' ? 'Randomized' : 'Unavailable') : null, s.note) : null,
    h(
      'details',
      { class: 'signal-info' },
      h('summary', null, 'What is this?'),
      h(
        'dl',
        null,
        h('dt', null, 'What it is'),
        h('dd', null, info.what),
        h('dt', null, 'Why it matters'),
        h('dd', null, info.why),
        info.tips.length ? [h('dt', null, 'How to reduce it'), h('dd', null, h('ul', null, info.tips.map((t) => h('li', null, t))))] : null,
      ),
      h('p', { class: 'signal-id' }, 'Signal id: ', h('code', null, s.id)),
    ),
  );
  return li;
}

function categoryMeta(cat: CategoryResult): string {
  if (cat.status === 'timeout') return 'timed out';
  if (cat.status === 'error') return 'failed';
  const high = cat.signals.filter((s) => bitsLevel(s.bits) === 'high').length;
  const randomized = cat.signals.filter((s) => s.randomized).length;
  const parts = [`${cat.signals.length} signal${cat.signals.length === 1 ? '' : 's'}`];
  if (high) parts.push(`${high} high`);
  if (randomized) parts.push(`${randomized} randomized`);
  return parts.join(' · ');
}

export function renderCategory(cat: CategoryResult, onWhatIf?: WhatIfHandler): HTMLDetailsElement {
  return h(
    'details',
    { class: 'category', id: `cat-${cat.id}`, 'data-status': cat.status },
    h(
      'summary',
      null,
      h('h3', { class: 'category-title' }, cat.title),
      h('span', { class: 'category-meta' }, categoryMeta(cat)),
    ),
    h(
      'div',
      { class: 'category-body' },
      h('p', { class: 'category-desc' }, cat.description),
      cat.status !== 'ok'
        ? h('p', { class: 'signal-note' }, `This category ${cat.status === 'timeout' ? 'did not finish within the time limit' : 'failed'}${cat.error ? ` (${cat.error})` : ''}. The rest of the report is unaffected.`)
        : null,
      cat.signals.length ? h('ul', { class: 'signals' }, cat.signals.map((s) => renderSignal(s, onWhatIf))) : null,
    ),
  );
}
